import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, fmtDate } from "@/lib/api";
import { Wallet, Plus, TrendingUp, History } from "lucide-react";

export default function ProjectFinancialSummary({ projectId }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/projects/${projectId}/financial-summary`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("finance:refresh", h);
    return () => window.removeEventListener("finance:refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (!data) return null;

  const pct = data.collection_pct;
  // Timeline shows BOTH directions: customer receipts (in) and vendor payouts (out).
  const timeline = (data.payments || []).filter((p) => p.status !== "Rejected");
  const receipts = timeline.filter((p) => p.kind === "Customer").length;
  const payouts = timeline.filter((p) => p.kind === "Vendor").length;

  return (
    <div className="flat-card" data-testid="project-financial-summary">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[#002FA7]" />
          <div className="label-eyebrow">Financial Summary</div>
        </div>
        <Link to="/finance" className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline">
          <TrendingUp className="w-3 h-3 inline mr-1" /> Open Finance
        </Link>
      </div>

      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Cell label="Contract Value" value={inr(data.contract_value)} />
          <Cell label="Amount Collected" value={inr(data.collected)} color="#10B981" />
          <Cell label="Pending Amount" value={inr(data.pending)} color="#DC2626" />
          <Cell label="Collection Progress" value={`${pct}%`} color="#002FA7" />
        </div>

        <div>
          <div className="h-3 w-full bg-[#F3F4F6]">
            <div className="h-full bg-[#10B981] transition-all" style={{ width: `${Math.min(pct, 100)}%` }} data-testid="financial-progress-bar" />
          </div>
          <div className="text-[10px] text-[#9CA3AF] mt-1 uppercase tracking-widest font-bold">
            {pct}% collected of {inr(data.contract_value)}
          </div>
        </div>

        {/* Payment Timeline — receipts (in) + payouts (out) */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow flex items-center gap-2">
              <History className="w-3 h-3" /> Payment Timeline
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]" data-testid="payment-timeline-count">
              {receipts} received · {payouts} paid out
            </span>
          </div>

          {timeline.length === 0 ? (
            <div className="text-xs text-[#9CA3AF] border border-dashed border-[#E5E7EB] py-6 text-center">
              No payments recorded yet. Use the <Link to="/finance" className="text-[#002FA7] underline">Finance page</Link> to add one.
            </div>
          ) : (
            <div className="border border-[#E5E7EB] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>
                    {["Date", "Type", "Stage / Note", "Amount", "Mode", "Reference", "Status"].map((h) => (
                      <th key={h} className="text-left label-eyebrow !text-[10px] px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeline.map((p) => {
                    const isIn = p.kind === "Customer";
                    return (
                      <tr key={p.id} className="border-b border-[#F3F4F6]" data-testid={`pmt-${p.id}`}>
                        <td className="px-3 py-2 text-xs">{fmtDate(p.tx_date || p.created_at)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${isIn ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`} data-testid={`pmt-type-${p.id}`}>
                            {isIn ? "↓ Received" : "↑ Paid Out"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className="font-semibold">{p.milestone || p.category || (isIn ? "Customer Payment" : "Vendor Payment")}</span>
                          {p.note && <div className="text-[10px] text-[#9CA3AF]">{p.note}</div>}
                        </td>
                        <td className={`px-3 py-2 text-xs font-bold ${isIn ? "text-[#065F46]" : "text-[#DC2626]"}`}>
                          {isIn ? "+" : "−"}{inr(p.amount_inr)}
                        </td>
                        <td className="px-3 py-2 text-xs">{p.method}</td>
                        <td className="px-3 py-2 text-xs font-mono">{p.reference || "—"}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${p.status === "Received" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEF3C7] text-[#92400E]"}`}>{p.status}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 text-[10px] text-[#9CA3AF]">
            <Plus className="w-3 h-3 inline mr-1" /> Add payments from the <Link to="/finance" className="underline">Finance</Link> page · receipts and payouts appear here automatically.
          </div>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, color = "#0a0a0a" }) {
  return (
    <div>
      <div className="label-eyebrow text-[9px]">{label}</div>
      <div className="text-xl font-black tracking-tight mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
